import {
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { personOutline, arrowForward } from 'ionicons/icons';
import type { Player } from '../../../types/graphql-generated';
import { PlayerCard } from '../components/PlayerCard';
import { ReleaseClauseBadge } from '../components/ReleaseClauseBadge';

export interface PastOfferItem {
  id: string;
  player: Player;
  amount: number;
  status: string;
  createdAt: string;
  sellerName: string;
  sellerMemberId?: string;
  bidderName: string;
  bidderMemberId?: string;
  releaseClause?: boolean;
}

interface PastOffersListProps {
  items: PastOfferItem[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore: (event: CustomEvent<void>) => void;
  emptyMessage?: string;
  onMemberClick?: (fantasyLeagueMemberId: string) => void;
}

const getStatusClass = (status: string): string => {
  switch (status) {
    case 'accepted':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'expired':
    case 'withdrawn':
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'accepted':
      return 'Aceptada';
    case 'rejected':
      return 'Rechazada';
    case 'expired':
      return 'Expirada';
    case 'withdrawn':
      return 'Retirada';
    default:
      return status;
  }
};

export const PastOffersList = ({
  items,
  hasMore,
  loading,
  onLoadMore,
  emptyMessage,
  onMemberClick,
}: PastOffersListProps) => {
  if (!loading && items.length === 0) {
    return (
      <IonText>
        <div className="text-center mt-8">
          <h2 className="text-xl font-semibold">No hay ofertas todavía</h2>
          {emptyMessage && (
            <p className="text-gray-500 mt-2">{emptyMessage}</p>
          )}
        </div>
      </IonText>
    );
  }

  return (
    <>
      {items.map((item) => {
        const offerDate = new Date(item.createdAt).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });

        return (
          <div key={item.id} className="mb-6">
            <PlayerCard
              player={item.player}
              footerDetails={
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    {item.sellerMemberId && onMemberClick ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onMemberClick(item.sellerMemberId!);
                        }}
                        aria-label={`Ver plantilla de ${item.sellerName}`}
                        className="inline-flex items-center gap-1.5 text-xs transition-colors hover:text-[var(--fc-green)]"
                      >
                        <IonIcon icon={personOutline} className="text-sm" />
                        {item.sellerName}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <IonIcon icon={personOutline} className="text-sm" />
                        {item.sellerName}
                      </span>
                    )}
                    <IonIcon icon={arrowForward} className="text-xs text-slate-400" />
                    {item.bidderMemberId && onMemberClick ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onMemberClick(item.bidderMemberId!);
                        }}
                        aria-label={`Ver plantilla de ${item.bidderName}`}
                        className="inline-flex items-center gap-1.5 text-xs transition-colors hover:text-[var(--fc-green)]"
                      >
                        <IonIcon icon={personOutline} className="text-sm" />
                        {item.bidderName}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <IonIcon icon={personOutline} className="text-sm" />
                        {item.bidderName}
                      </span>
                    )}
                    {item.releaseClause && <ReleaseClauseBadge />}
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-500">{offerDate}</span>

                    <div className="flex items-center gap-2">
                      <div className={`rounded-lg px-3 py-1 font-semibold text-xs ${getStatusClass(item.status)}`}>
                        {getStatusLabel(item.status)}
                      </div>
                      <div className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-900">
                        {item.amount.toLocaleString('es-ES')}€
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
        );
      })}

      <IonInfiniteScroll
        onIonInfinite={onLoadMore}
        threshold="100px"
        disabled={!hasMore || loading}
      >
        <IonInfiniteScrollContent
          loadingSpinner="bubbles"
          loadingText="Cargando más ofertas..."
        />
      </IonInfiniteScroll>

      {loading && items.length > 0 && (
        <div className="flex justify-center py-4">
          <IonSpinner name="crescent" />
        </div>
      )}
    </>
  );
};
